import { useCallback, useEffect, useRef, useState } from "react";

export const PAGE_SIZE = 20;
// Tanda de los feeds principales (ofertas, eventos, autos y viajes).
export const FEED_PAGE_SIZE = 13;

// Scroll infinito del lado del render: las secciones ya tienen la lista
// completa (filtrada y ordenada en el cliente), así que en vez de paginar la
// query se muestran de a `pageSize` y se suman otros `pageSize` cuando el
// sentinel (colocado después del último ítem visible) se acerca a la
// pantalla. `resetKey` vuelve a la primera página cuando cambian los
// filtros/búsqueda, para no arrastrar el límite de la lista anterior.
//
// Uso:
//   const { limit, sentinelRef } = useInfiniteScroll(filtersKey);
//   items.slice(0, limit).map(...)
//   {items.length > limit && <Box ref={sentinelRef} sx={{ height: 1 }} />}
const useInfiniteScroll = (resetKey, pageSize = PAGE_SIZE) => {
  const [limit, setLimit] = useState(pageSize);
  const observerRef = useRef(null);

  useEffect(() => {
    setLimit(pageSize);
  }, [resetKey, pageSize]);

  const sentinelRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) setLimit((l) => l + pageSize);
        },
        // Carga antes de llegar al final: cuando el usuario está viendo la
        // última publicación, la siguiente tanda ya está en camino.
        { rootMargin: "0px 0px 800px 0px" }
      );
      observerRef.current.observe(node);
    },
    // `limit` en las deps: al crecer la lista el ref se vuelve a conectar y
    // el observer reevalúa, así si el sentinel sigue visible (ítems cortos)
    // se carga otra tanda en vez de quedarse trabado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, limit]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { limit, sentinelRef };
};

export default useInfiniteScroll;
